import { ORG_APIS, USER_APIS } from "@/api_framework/api_config";
import { IOrgCityListResponse, IOrgCityListResponseData } from "@/api_framework/api_modals/Org";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";

import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { setConfOrgCities } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { setEMROrgCities } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { setInventoryOrgCities } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { setOrgCities } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { setROTAOrgCities } from "@/app_redux/reducers/slices/auth/rota_slice";
import { setSlotsOrgCities } from "@/app_redux/reducers/slices/auth/slots_slice";
import { setCALENDAROrgCities } from "@/app_redux/reducers/slices/auth/staff_calendar_slice";
import { setUIOrgCities } from "@/app_redux/reducers/slices/ui_states_slice";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { ConfigurationOptionsClinicAPI, useStaffClinicListAPI } from "../clinic/useStaffClinicListAPI";
import { IUserRoleResponse, IUserRoleResponseData } from "@/api_framework/api_modals/UserRoles";
// Only needed by the forced-logout blocks below, which are disabled.
// import { useFirebaseLogin } from "../auth/useFirebaseLogin";
// import { useRouter } from "../../useRouter";
import { setUserPermissions } from "@/app_redux/reducers/auth_reducer";

AxiosCurlirize(axios, () => {});
type OrgCitiesListAPIClinicOptions = {
  cityId?: string;
  cityIds?: string[];
  selectFirst?: boolean;
  withMappedClinic?: boolean;
  selectAll?: boolean;
};

export type AllowedCityClinicProviderRoutes =
  | "ROTA"
  | "SLOTS"
  | "PATIENT_WAITING_LIST"
  | "CONFIGURATION"
  | "INVENTORY"
  | "EMR"
  | "UI"
  | "CALENDAR";

export type CongfigurationOptionsCitiesAPI = {
  withMappedCity?: boolean;
  withClinics: boolean;
  withClinicProviders?: boolean;
  ROTADate?: string;
  staffCategoryId?: string;
  clinicOptions?: OrgCitiesListAPIClinicOptions;
};

export const useOrgCitiesListAPI = (forRoute: AllowedCityClinicProviderRoutes) => {
  const configuration = useConfiguration();
  // Only used by the forced-logout blocks below, which are disabled.
  // const { clientSignOut } = useFirebaseLogin();
  // const { getRouteKey } = useRouter();
  const dispatch = useAppDispatch();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const fetchOrgClinics = useStaffClinicListAPI(forRoute);

  const cityId = localStorage.getItem(configuration.localStorage.CITY.key);

  const execute = useCallback(
    async (configurationOptions?: CongfigurationOptionsCitiesAPI) => {
      dispatch(
        setLoading({
          key: "isOrgCitiesInProgress",
          status: true,
        }),
      );
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const response: any = await axios
          .get(USER_APIS?.USER_ROLE?.baseURL ?? "", {
            params: {},
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then(async (res: AxiosResponse<IUserRoleResponse>) => {
            dispatch(
              setLoading({
                key: "isOrgCitiesInProgress",
                status: false,
              }),
            );
            if (res.data.status === true) {
              dispatch(setUserPermissions(res.data.data?.staff_role_details));
              if (configurationOptions?.withClinics) {
                let cityIds = [];

                if (configurationOptions?.clinicOptions) {
                  const { clinicOptions } = configurationOptions;

                  if (clinicOptions?.selectFirst && clinicOptions?.selectFirst === true) {
                    cityIds = [res?.data?.data?.assigned_cities_clinics[0]?.id];
                  } else if (clinicOptions?.withMappedClinic && clinicOptions?.withMappedClinic === true) {
                    cityIds = [res?.data?.data?.assigned_cities_clinics?.filter((c) => c.id === cityId)[0].id];
                  } else if (clinicOptions?.selectAll && clinicOptions?.selectAll === true) {
                    cityIds = res.data.data?.assigned_cities_clinics?.map((c) => c.id);
                  } else if (clinicOptions?.cityId && clinicOptions?.cityId !== "") {
                    cityIds = [clinicOptions?.cityId];
                  } else if (clinicOptions?.cityIds && clinicOptions?.cityIds?.length !== 0) {
                    cityIds = clinicOptions?.cityIds;
                  } else {
                    // No Case Is Valid
                    cityIds = [res?.data?.data?.assigned_cities_clinics[0]?.id];
                  }
                  // Fetch Org Clinics According to cityIds
                } else {
                  cityIds = res.data.data?.assigned_cities_clinics?.map((c) => c.id);
                }

                let clinicAPIConfig: ConfigurationOptionsClinicAPI = {
                  cityIds: cityIds,
                  withProviders: configurationOptions?.withClinicProviders ?? true,
                  staffCategoryId: configurationOptions?.staffCategoryId,
                  clinicOptions: {
                    selectFirst: configurationOptions?.clinicOptions?.selectFirst ?? true,
                  },
                };

                if (configurationOptions?.ROTADate && configurationOptions?.ROTADate !== "") {
                  clinicAPIConfig = {
                    ...clinicAPIConfig,
                    ROTADate: configurationOptions?.ROTADate,
                  };
                }
                fetchOrgClinics({
                  ...clinicAPIConfig,
                });
              }
              
              
              switch (forRoute) {
                case "UI":
                  dispatch(
                    setUIOrgCities({
                      data: res.data.data?.assigned_cities_clinics,
                    }),
                  );
                  break;
                case "EMR":
                  dispatch(
                    setEMROrgCities({
                      data: res.data.data?.assigned_cities_clinics,
                    }),
                  );
                  break;
                case "INVENTORY":
                  dispatch(
                    setInventoryOrgCities({
                      data: res.data.data?.assigned_cities_clinics,
                    }),
                  );
                  break;
                case "ROTA":
                  dispatch(
                    setROTAOrgCities({
                      data: res.data.data?.assigned_cities_clinics, // Change it for ROTA
                    }),
                  );
                  break;
                case "CALENDAR":
                  dispatch(
                    setCALENDAROrgCities({
                      data: res.data.data?.assigned_cities_clinics, // Change it for CALENDAR
                    }),
                  );
                  break;

                case "PATIENT_WAITING_LIST":
                  dispatch(
                    setOrgCities({
                      data: res.data.data?.assigned_cities_clinics,
                    }),
                  );
                  break;

                case "CONFIGURATION":
                  dispatch(
                    setConfOrgCities({
                      data: res.data.data?.assigned_cities_clinics,
                    }),
                  );
                  break;

                case "SLOTS":
                  dispatch(
                    setSlotsOrgCities({
                      data: res.data.data?.assigned_cities_clinics,
                    }),
                  );
                  break;
                default:
                  dispatch(setOrgCities({ data: res.data.data?.assigned_cities_clinics }));
                  break;
              }
              return res.data.data;
            } else {
              dispatch(
                setOrgCities({
                  data: [],
                }),
              );
              // Forced logout disabled — a `status: false` body is not proof
              // the session is invalid. Genuine 401s are handled centrally by
              // the interceptor in setupAxios.ts.
              // clientSignOut();
              // window.open(getRouteKey("LOGIN_PAGE", "url"), "_self");
              return [];
            }
          })
          .catch((e: AxiosError) => {
            sendCurl(e.config["curlCommand"]);
            captureError(e);
            // Forced logout disabled — this catch fires on ANY failure: a 500,
            // a timeout, a dropped clinic wifi connection. None of those mean
            // the session is over, and signing the user out here threw doctors
            // back to the login screen on a transient network blip.
            // clientSignOut();
            // window.open(getRouteKey("LOGIN_PAGE", "url"), "_self");
            return [];
          });
        return response;
      } catch (e: any) {
        // Forced logout disabled — see above.
        // clientSignOut();
        // window.open(getRouteKey("LOGIN_PAGE", "url"), "_self");
        toast.error("Server Error: " + e.message);
        return [];
      }
    },
    [dispatch],
  );
  return execute;
};
